import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@material-ui/core";
import SummaryPatientHeader from "../OrderHistory/OrderTable/SummaryPatientHeader";
import { makeStyles } from "@material-ui/core";
import { colors } from "@bg/shared/lib/theme";
import { Cancel } from "@material-ui/icons";
import { PrimaryButton, SecondaryButton } from "@bg/shared/lib/components";

const useStyles = makeStyles({
  root: {
    "& .MuiInputBase-input": {
      backgroundColor: colors.white,
    },
  },
  buttonContainer: {
    paddingTop: ".75em",
    paddingBottom: ".75em",
    width: "780px",
    backgroundColor: colors.grey,
  },
  floatRight: {
    float: "right",
  },
  redColor: {
    color: colors.candyRed,
  },
  patientName: {
    fontWeight: 700,
  },
  closeDialogButton: {
    textTransform: "none",
    color: colors.white,
    backgroundColor: colors.mediumBrown,
    "&:hover": {
      background: darken(colors.mediumBrown, 0.1),
    },
  },
  inputField: {
    padding: "0 24px 0 24px",
  },
  bottomBtnContainer: {
    backgroundColor: colors.grey,
  },
});

interface CancellationDialogProps {
  openDialog: boolean;
  orderInfo?: SummaryPatientHeader;
  closeDialog: () => void;
  cancelOrder: (
    id: number,
    cancelReason: string,
    type: string,
    hospitalCode?: string,
  ) => void;
}

const CancellationDialog: FunctionComponent<CancellationDialogProps> = ({
  openDialog,
  orderInfo,
  closeDialog,
  cancelOrder,
}) => {
  const [cancellationReason, setCancellationReason] = useState("");
  const [authorize, setAuthorize] = useState<boolean>(false);

  useEffect(() => {
    if (!openDialog) {
      setCancellationReason("");
      setAuthorize(false);
    }
  }, [openDialog]);

  const handleCancelOrder = () => {
    if (orderInfo && orderInfo.id) {
      const orderId = parseInt(orderInfo.id);
      const type = orderInfo.displayStatus !== "En Route" ? "Sample" : "Order";
      cancelOrder(orderId, cancellationReason, type, orderInfo.hospitalCode);
    }
  };

  const handleChange = () => {
    setAuthorize(!authorize);
  };
  const classes = useStyles();

  return (
    <Dialog open={openDialog} onClose={closeDialog} maxWidth="lg">
      <div className={classes.buttonContainer}>
        <Button className={classes.floatRight} onClick={closeDialog}>
          <Cancel className={classes.redColor} fontSize="medium" />
        </Button>
        <DialogTitle id="form-dialog-title">Confirm Cancellation</DialogTitle>
        <DialogContent>
          {orderInfo && (
            <DialogContentText>
              Are you sure you want to cancel the {orderInfo.testName} test for{" "}
              <span className={classes.patientName}>
                {orderInfo.lastName}, {orderInfo.firstName}
              </span>
              ?
            </DialogContentText>
          )}
        </DialogContent>
        <div className={classes.inputField}>
          <TextField
            value={cancellationReason}
            classes={{ root: classes.root }}
            onChange={(e) => setCancellationReason(e.target.value)}
            type="string" // tell TextField to render select
            label="Cancellation Reason"
            multiline={true}
            minRows={3}
            required
            fullWidth
          />
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  id="authorize-checkbox"
                  name="authorize-checkbox"
                  checked={authorize}
                  onChange={handleChange}
                  required
                />
              }
              label="I authorize Baylor Miraca Genetics Laboratories to cancel this testing at my request."
            />
          </div>
        </div>
      </div>
      <DialogActions className={classes.bottomBtnContainer}>
        <SecondaryButton onClick={closeDialog}>Close</SecondaryButton>
        <PrimaryButton
          onClick={handleCancelOrder}
          style={{ marginRight: "16px" }}
          disabled={!(cancellationReason && authorize)}
        >
          Yes, cancel order
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default CancellationDialog;
